// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SearchAutoCompleteProductAction from "@hardwoods/Overrides/Components/SearchAutoCompleteProductAction";
import { color, ellipsis } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton, {
    productAddToCartButtonStyles,
} from "@insite/content-library/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import Button from "@insite/mobius/Button/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

export interface CustomProductAutocompleteItemModel extends ProductAutocompleteItemModel {
    canAddToCart: boolean;
    minimumOrderQty: number;
    canEnterQuantity: boolean;
    isVariantParent: boolean;
}

interface Props {
    products: CustomProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;

    /** <XNG-Changes> */
    goToSearchUrl: () => void;
    searchQuery?: string;
    showAddToCart?: boolean;
    /** </XNG-Changes> */
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;

    // XNG-Changes
    headerWrapper?: InjectableCss;
    shopAllProductsLink?: LinkPresentationProps;
    mainContainer?: InjectableCss;
    productAction?: any;
    erpNumberLabel?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 90px;
            max-height: 90px;
            flex-basis: 90px;
        `,
    },
    image: {
        width: "90px",
        height: "90px",
        errorTypographyProps: {
            css: css`
                max-height: 100%;
            `,
        },
        css: css`
            img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        size: 14,
        css: css`
            width: 100%;
            display: inline-flex;
            text-align: left;
            margin: 8px 0 0 0;
            letter-spacing: 0;
            line-height: 18px;
            ${xScreen.lte("xs")} {
                display: flex;
                justify-content: center;
            }
        `,
    },
};

const customAutocompleteProductsStyles = {
    headerWrapper: {
        css: css`
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 1px solid ${color("common.border")};

            width: 100%;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            ${xScreen.lte("xs")} {
                margin-right: 0;
            }
        `,
    },
    shopAllProductsLink: {
        typographyProps: {
            size: 12,
            lineHeight: "18px",
            weight: "bold",
            css: css`
                letter-spacing: 0;
            `,
        },
        css: css`
            display: block;
            width: 100%;
            text-align: left;
        `,
    },
    headerText: {
        weight: "normal",
        size: 15,
        css: css`
            text-align: left;
            margin: 0 0 10px 0;
            letter-spacing: 0;
            line-height: 27px;
        `,
    },
    mainContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
        `,
    },
    container: {
        css: css`
            display: flex;
            border-bottom: 1px solid ${color("common.border")};
            padding: 20px 0 15px;
            width: 100%;
        `,
    },
    infoGridItem: {
        css: css`
            margin-left: 20px;
            text-align: left;
            display: flex;
            flex-direction: column;
            max-width: calc(100% - 110px);
            flex-basis: calc(100% - 110px);
            ${xScreen.lte("xs")} {
                flex-basis: 100%;
                max-width: 100%;
                margin-left: 0;
                align-items: center;
                text-align: center;
            }
        `,
    },
    titleLink: {
        typographyProps: {
            size: 15,
            color: "text.secondary",
            css: css`
                letter-spacing: 0;
                line-height: 20px;
                text-align: left;

                width: 100%;
                margin: 0;
                ${ellipsis(2)}
            `,
        },
        css: css`
            min-height: 40px;
            align-items: flex-start;

            ${TypographyStyle} {
                color: ${color("text.secondary")};
                font-size: 15px;
                letter-spacing: 0;
                line-height: 20px;
            }
        `,
    },
    productAction: {
        wrapper: {
            css: css`
                display: flex;
                justify-content: flex-start;
                align-items: flex-end;
                margin-top: 12px;
                ${xScreen.lte("xs")} {
                    justify-content: center;
                }
            `,
        },
        qty: {
            qtyField: {
                cssOverrides: {
                    formField: css`
                        width: 50px;
                        margin-right: 20px;
                    `,
                    formInputWrapper: css`
                        min-width: 0;
                    `,
                },
            },
        },
        addToCart: {
            css: css`
                flex: 1;
                min-height: 40px;
            `,
        },
    },
    erpNumberLabel: {
        css: css`
            font-size: inherit;
            line-height: inherit;
            letter-spacing: inherit;

            display: block;
            font-weight: bold;
            margin-right: 5px;
        `,
    },
};

const styles = { ...autocompleteProductsStyles, ...customAutocompleteProductsStyles };

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { searchQuery, products, showAddToCart } = this.props;
        const styles = this.styles;
        return (
            <>
                <StyledWrapper {...styles.headerWrapper}>
                    <Typography {...styles.headerText}>
                        {translate("Recommended Products for")}
                        <Typography
                            size={22}
                            css={css`
                                display: block;
                                font-weight: bold;
                            `}
                        >
                            {searchQuery}
                        </Typography>
                    </Typography>
                    <Link onClick={this.props.goToSearchUrl} {...styles.shopAllProductsLink}>
                        {translate("Shop All Recommended Products")}
                    </Link>
                </StyledWrapper>

                <StyledWrapper {...styles.mainContainer}>
                    {products.map(product => (
                        <StyledWrapper
                            key={product.id!}
                            {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        >
                            <StyledWrapper {...styles.imageGridItem}>
                                <Link {...styles.titleLink} onClick={() => this.props.goToUrl(product.url)}>
                                    <LazyImage
                                        {...styles.image}
                                        src={product.image}
                                        altText={product.title || product.name}
                                    />
                                </Link>
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoGridItem}>
                                <Link {...styles.titleLink} onClick={() => this.props.goToUrl(product.url)}>
                                    {product.title}
                                </Link>
                                {product.erpNumber && !product.isVariantParent && (
                                    <Typography {...styles.erpNumberText}>
                                        <Typography {...styles.erpNumberLabel}>{translate("Part #")}:</Typography>
                                        {product.erpNumber}
                                    </Typography>
                                )}

                                {showAddToCart && (
                                    <SearchAutoCompleteProductAction product={product}>
                                        {!product.isVariantParent ? (
                                            <SearchAutoCompleteProductAction product={product}>
                                                <StyledWrapper {...styles.productAction.wrapper}>
                                                    <ProductQuantityOrdered
                                                        id={`${product.id}-qty`}
                                                        label=""
                                                        extendedStyles={{
                                                            ...styles.productAction.qty,
                                                        }}
                                                    />
                                                    <ProductAddToCartButton
                                                        data-test-selector={`actionsAddToCart${product.id}`}
                                                        extendedStyles={styles.productAction.addToCart}
                                                    />
                                                </StyledWrapper>
                                            </SearchAutoCompleteProductAction>
                                        ) : (
                                            <Button
                                                {...productAddToCartButtonStyles}
                                                onClick={() => this.props.goToUrl(product.url)}
                                                sizeVariant="small"
                                                style={{ width: "200px", marginTop: "12px" }}
                                            >
                                                {translate("Choose Options")}
                                            </Button>
                                        )}
                                    </SearchAutoCompleteProductAction>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </>
        );
    }
}

export default AutocompleteProducts;
